import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'

export interface TabItem<T extends string> {
  value: T
  label: string
  count?: number
  icon?: ReactNode
}

/**
 * Tab sederhana berbasis tombol, lengkap dengan peran ARIA.
 *
 * Bentuk "garis" membagi lebar rata dengan garis bawah pada yang aktif —
 * bentuk tab di lini masa dan profil. Bentuk "pil" untuk penyaring di
 * dalam halaman kerja.
 */
export function Tabs<T extends string>({
  items,
  value,
  onChange,
  className,
  variant = 'pil',
}: {
  items: TabItem<T>[]
  value: T
  onChange: (value: T) => void
  className?: string
  variant?: 'pil' | 'garis'
}) {
  if (variant === 'garis') {
    return (
      <div role="tablist" className={cn('flex border-b border-line', className)}>
        {items.map((item) => {
          const active = item.value === value
          return (
            <button
              key={item.value}
              role="tab"
              type="button"
              aria-selected={active}
              onClick={() => onChange(item.value)}
              className={cn(
                '-mb-px flex flex-1 items-center justify-center gap-1.5 border-b px-2 py-3 text-[15px] font-semibold transition',
                active ? 'border-ink text-ink' : 'border-transparent text-subtle hover:text-ink',
              )}
            >
              {item.icon}
              {item.label}
              {typeof item.count === 'number' && item.count > 0 ? (
                <span className="text-xs tabular-nums text-subtle">{item.count}</span>
              ) : null}
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <div role="tablist" className={cn('flex flex-wrap gap-1 rounded-xl bg-sunken p-1', className)}>
      {items.map((item) => {
        const active = item.value === value
        return (
          <button
            key={item.value}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(item.value)}
            className={cn(
              'inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-semibold transition',
              active ? 'bg-raised text-ink shadow-sm' : 'text-muted hover:text-ink',
            )}
          >
            {item.icon}
            {item.label}
            {typeof item.count === 'number' ? (
              <span
                className={cn(
                  'rounded-full px-1.5 py-0.5 text-xs font-bold tabular-nums',
                  active ? 'bg-brand-soft text-brand-ink' : 'bg-line text-muted',
                )}
              >
                {item.count}
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
